import { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../components/LanguageSelector';
import { Button } from '../components/ui/button';
import { ArrowLeft, Plane, Clock, UserCheck, Briefcase, MapPin, Shield, Leaf, Car, Check } from 'lucide-react';

const SEO_ROUTES = {
  'vtc-aeroport-nice': {
    fr: { h1: 'VTC Aéroport Nice Côte d\'Azur — Chauffeur Privé Tesla', desc: 'Transfert aéroport Nice prix fixe garanti. Chauffeur privé Tesla disponible 7j/7. Aucune surprise tarifaire.' },
    en: { h1: 'Nice Airport VTC — Private Tesla Chauffeur', desc: 'Nice airport transfer fixed price guaranteed. Private Tesla driver available 7 days a week. No hidden fees.' },
    pickup: 'Aéroport Nice Côte d\'Azur (NCE)',
    destination: 'Nice Centre',
    price: '25',
    roundTrip: '45',
    distance: '10 km',
    duration: '15-25 min',
  },
  'transfert-aeroport-nice-monaco': {
    fr: { h1: 'Transfert Aéroport Nice → Monaco — VTC Privé Tesla', desc: 'Transfert aéroport Nice Monaco prix fixe garanti. Chauffeur privé Tesla disponible 7j/7. Aucune surprise tarifaire.' },
    en: { h1: 'Nice Airport → Monaco Transfer — Private Tesla VTC', desc: 'Nice airport to Monaco transfer fixed price guaranteed. Private Tesla driver available 7 days a week. No hidden fees.' },
    pickup: 'Aéroport Nice Côte d\'Azur (NCE)',
    destination: 'Monaco / Monte-Carlo',
    price: '65',
    roundTrip: '120',
    distance: '30 km',
    duration: '30-45 min',
  },
  'transfert-aeroport-nice-cannes': {
    fr: { h1: 'Transfert Aéroport Nice → Cannes — VTC Chauffeur Privé', desc: 'Transfert aéroport Nice Cannes prix fixe garanti. Chauffeur privé Tesla disponible 7j/7. Aucune surprise tarifaire.' },
    en: { h1: 'Nice Airport → Cannes Transfer — Private Chauffeur VTC', desc: 'Nice airport to Cannes transfer fixed price guaranteed. Private Tesla driver available 7 days a week. No hidden fees.' },
    pickup: 'Aéroport Nice Côte d\'Azur (NCE)',
    destination: 'Cannes / La Croisette',
    price: '60',
    roundTrip: '110',
    distance: '38 km',
    duration: '35-50 min',
  },
  'transfert-aeroport-nice-saint-tropez': {
    fr: { h1: 'Transfert Aéroport Nice → Saint-Tropez — VTC Premium Tesla', desc: 'Transfert aéroport Nice Saint-Tropez prix fixe garanti. Chauffeur privé Tesla disponible 7j/7. Aucune surprise tarifaire.' },
    en: { h1: 'Nice Airport → Saint-Tropez Transfer — Premium Tesla VTC', desc: 'Nice airport to Saint-Tropez transfer fixed price guaranteed. Private Tesla driver available 7 days a week. No hidden fees.' },
    pickup: 'Aéroport Nice Côte d\'Azur (NCE)',
    destination: 'Saint-Tropez',
    price: '180',
    roundTrip: '300',
    roundTripCondition: true,
    distance: '110 km',
    duration: '1h30-2h',
  },
  'vtc-aeroport-nice-centre': {
    fr: { h1: 'VTC Aéroport Nice → Nice Centre-Ville — Transfert Tesla', desc: 'Transfert aéroport Nice centre-ville prix fixe garanti. Chauffeur privé Tesla disponible 7j/7. Aucune surprise tarifaire.' },
    en: { h1: 'Nice Airport → Nice City Centre VTC — Tesla Transfer', desc: 'Nice airport to city centre transfer fixed price guaranteed. Private Tesla driver available 7 days a week. No hidden fees.' },
    pickup: 'Aéroport Nice Côte d\'Azur (NCE)',
    destination: 'Nice Centre-Ville',
    price: '25',
    roundTrip: '45',
    distance: '10 km',
    duration: '15-25 min',
  },
};

const advantages = [
  { icon: Car, fr: 'Tesla Model Y 100% électrique', en: 'Tesla Model Y 100% electric' },
  { icon: Plane, fr: 'Suivi de vol en temps réel', en: 'Real-time flight tracking' },
  { icon: UserCheck, fr: 'Accueil avec pancarte nominative', en: 'Welcome with name sign' },
  { icon: Clock, fr: '30 minutes d\'attente gratuites', en: '30 minutes free waiting time' },
  { icon: Briefcase, fr: 'Bagages inclus dans le prix', en: 'Luggage included in price' },
  { icon: Shield, fr: 'Chauffeur professionnel agréé', en: 'Licensed professional driver' },
  { icon: Leaf, fr: 'Empreinte carbone zéro', en: 'Zero carbon footprint' },
];

export const SeoLandingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const slug = location.pathname.replace('/', '');
  const data = SEO_ROUTES[slug];
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  useEffect(() => {
    if (data) {
      document.title = `${data[lang].h1} | YOUMI GREEN VTC`;
      let canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.href = `https://youmigreenvtc.com/${slug}`;
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.content = data[lang].desc;
    }
  }, [slug, lang, data]);

  const [roundTrip, setRoundTrip] = useState(false);

  if (!data) return <div className="min-h-screen bg-[#09090b] flex items-center justify-center text-white">Page non trouvée</div>;

  const activePrice = roundTrip ? data.roundTrip : data.price;

  const handleBook = () => {
    navigate(`/quick-booking?pickup=${encodeURIComponent(data.pickup)}&destination=${encodeURIComponent(data.destination)}&price=${activePrice}&roundtrip=${roundTrip ? '1' : '0'}`);
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      <header className="sticky top-0 z-50 bg-[#09090b]/95 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-white transition">
            <ArrowLeft className="w-5 h-5" />
            <img src="/logo-youmi.png" alt="YOUMI GREEN VTC" className="w-10 h-10" />
            <span className="text-lg font-bold text-white font-['Outfit']">YOUMI <span className="text-emerald-500">GREEN</span></span>
          </Link>
          <LanguageSelector variant="minimal" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-12">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Outfit'] mb-6 leading-tight" data-testid="seo-h1">
          {data[lang].h1}
        </h1>
        <p className="text-lg text-zinc-400 mb-10 max-w-3xl" data-testid="seo-desc">{data[lang].desc}</p>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Price card */}
          <div className="glass-card rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <MapPin className="w-7 h-7 text-emerald-500" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">{lang === 'fr' ? 'Trajet' : 'Route'}</p>
                <p className="text-white font-semibold">{data.pickup} → {data.destination}</p>
              </div>
            </div>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Distance</span>
                <span className="text-white">{data.distance}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">{lang === 'fr' ? 'Durée estimée' : 'Estimated duration'}</span>
                <span className="text-white">{data.duration}</span>
              </div>
              <div className="pt-3 border-t border-zinc-700">
                <label className="flex items-center gap-3 cursor-pointer mb-3 p-3 rounded-lg hover:bg-zinc-800/50 transition">
                  <input type="checkbox" checked={roundTrip} onChange={(e) => setRoundTrip(e.target.checked)} className="w-5 h-5 accent-emerald-500 rounded" data-testid="round-trip-checkbox" />
                  <span className="text-zinc-300 text-sm">{lang === 'fr' ? 'Aller-retour' : 'Round trip'}</span>
                </label>
                {data.roundTripCondition && roundTrip && (
                  <p className="text-xs text-zinc-500 italic mb-2">* {lang === 'fr' ? 'Aller-retour le même jour uniquement' : 'Same day round trip only'}</p>
                )}
                <div className="flex justify-between items-center p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                  <span className="text-emerald-400 font-semibold">{roundTrip ? (lang === 'fr' ? 'Aller-retour' : 'Round trip') : (lang === 'fr' ? 'Aller simple' : 'One way')}</span>
                  <span className="text-3xl font-bold text-emerald-500 font-['Outfit']">{activePrice}€</span>
                </div>
              </div>
            </div>
            <Button onClick={handleBook} className="w-full btn-primary text-lg py-6" data-testid="seo-book-btn">
              <Car className="w-5 h-5 mr-2" />
              {lang === 'fr' ? 'Réserver ce transfert' : 'Book this transfer'}
            </Button>
          </div>

          {/* Advantages */}
          <div className="glass-card rounded-2xl p-8">
            <h2 className="text-lg font-semibold text-white mb-6 font-['Outfit']">
              {lang === 'fr' ? 'Nos avantages' : 'Our advantages'}
            </h2>
            <div className="space-y-4">
              {advantages.map((a, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <a.icon className="w-4 h-4 text-emerald-500" />
                  </div>
                  <span className="text-zinc-300 text-sm">{a[lang]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Price guarantee */}
        <div className="p-5 bg-emerald-500/10 rounded-xl border border-emerald-500/30 text-center mb-8">
          <div className="flex items-center justify-center gap-2 text-emerald-400 font-semibold mb-2">
            <Shield className="w-5 h-5" />
            {lang === 'fr' ? 'Prix fixe garanti — Aucune surprise tarifaire' : 'Guaranteed fixed price — No pricing surprises'}
          </div>
          <p className="text-zinc-400 text-sm">{lang === 'fr' ? 'Votre prix est confirmé dès la réservation et ne changera jamais, même en cas de retard de vol, embouteillages ou conditions météo. Contrairement aux taxis et Uber, nous ne pratiquons aucun tarif dynamique.' : 'Your fare is confirmed at booking and will never change, even in case of flight delay, traffic or weather conditions. Unlike taxis and Uber, we never apply dynamic pricing.'}</p>
        </div>

        {/* FAQ-style content for SEO */}
        <div className="glass-card rounded-2xl p-8 mb-8">
          <h2 className="text-lg font-semibold text-white mb-4 font-['Outfit']">
            {lang === 'fr' ? 'Comment ça marche ?' : 'How does it work?'}
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { step: '1', fr: 'Réservez en ligne en 30 secondes. Renseignez vos informations et votre vol.', en: 'Book online in 30 seconds. Enter your details and flight number.' },
              { step: '2', fr: 'Votre chauffeur suit votre vol et vous attend à la sortie avec une pancarte à votre nom.', en: 'Your driver tracks your flight and waits at the exit with a sign with your name.' },
              { step: '3', fr: 'Installez-vous dans votre Tesla Model Y et profitez du trajet en toute sérénité.', en: 'Settle into your Tesla Model Y and enjoy a relaxed ride.' },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-3 text-black font-bold">{s.step}</div>
                <p className="text-zinc-400 text-sm">{s[lang]}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-zinc-800 py-6 text-center text-zinc-500 text-sm">
        <p>YOUMI GREEN VTC — SASU — {lang === 'fr' ? 'Transport privé premium 100% électrique' : 'Premium 100% electric private transport'}</p>
        <div className="flex justify-center gap-4 mt-2">
          <Link to="/mentions-legales" className="hover:text-emerald-400">{lang === 'fr' ? 'Mentions légales' : 'Legal'}</Link>
          <Link to="/cgv" className="hover:text-emerald-400">CGV</Link>
          <Link to="/confidentialite" className="hover:text-emerald-400">{lang === 'fr' ? 'Confidentialité' : 'Privacy'}</Link>
        </div>
      </footer>
    </div>
  );
};

export default SeoLandingPage;
